import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, Tag, AlertTriangle, CheckCircle } from 'lucide-react';

interface AgentRule {
  id: string;
  company_config_id: string;
  rule_type: 'must_do' | 'must_not_do' | 'conditional' | 'escalation' | 'compliance';
  title: string;
  description: string;
  priority: number;
  is_active: boolean;
  applies_to_agents: string[];
  conditions: any;
  actions: any;
  created_at: string;
  updated_at: string;
  company_config?: {
    name: string;
  };
}

interface Agent {
  id: string;
  name: string;
}

const AgentRulesPage: React.FC = () => {
  const [rules, setRules] = useState<AgentRule[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AgentRule | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  // Form state
  const [formData, setFormData] = useState({
    company_config_id: '',
    rule_type: 'must_do' as AgentRule['rule_type'],
    title: '',
    description: '',
    priority: 5,
    is_active: true,
    applies_to_agents: [] as string[],
    conditions: {},
    actions: {}
  });

  useEffect(() => {
    fetchAgents();
    fetchRules();
  }, []);

  const fetchAgents = async () => {
    const { data, error } = await supabase
      .from('company_configs')
      .select('id, company_name')
      .eq('is_active', true)
      .order('company_name');

    if (error) {
      console.error('Error fetching agents:', error);
    } else {
      setAgents(data?.map((a: any) => ({ id: a.id, name: a.company_name })) || []);
    }
  };

  const fetchRules = async () => {
    setLoading(true);
    let query = supabase
      .from('agent_rules')
      .select(`
        *,
        company_config:company_configs!agent_rules_company_config_id_fkey(name:company_name)
      `)
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching rules:', error);
    } else {
      setRules(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const ruleData = {
      ...formData,
      updated_at: new Date().toISOString()
    };

    if (editingRule) {
      const { error } = await supabase
        .from('agent_rules')
        .update(ruleData)
        .eq('id', editingRule.id);

      if (error) {
        console.error('Error updating rule:', error);
        alert('Failed to update rule');
      } else {
        await fetchRules();
        closeModal();
      }
    } else {
      const { error } = await supabase
        .from('agent_rules')
        .insert([{ ...ruleData, created_at: new Date().toISOString() }]);

      if (error) {
        console.error('Error creating rule:', error);
        alert('Failed to create rule');
      } else {
        await fetchRules();
        closeModal();
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    const { error } = await supabase
      .from('agent_rules')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule');
    } else {
      await fetchRules();
    }
  };

  const openModal = (rule?: AgentRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        company_config_id: rule.company_config_id,
        rule_type: rule.rule_type,
        title: rule.title,
        description: rule.description,
        priority: rule.priority,
        is_active: rule.is_active,
        applies_to_agents: rule.applies_to_agents || [],
        conditions: rule.conditions || {},
        actions: rule.actions || {}
      });
    } else {
      setEditingRule(null);
      setFormData({
        company_config_id: agents[0]?.id || '',
        rule_type: 'must_do',
        title: '',
        description: '',
        priority: 5,
        is_active: true,
        applies_to_agents: [],
        conditions: {},
        actions: {}
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  const getRuleTypeColor = (type: string) => {
    switch (type) {
      case 'must_do': return 'bg-green-100 text-green-800';
      case 'must_not_do': return 'bg-red-100 text-red-800';
      case 'conditional': return 'bg-blue-100 text-blue-800';
      case 'escalation': return 'bg-orange-100 text-orange-800';
      case 'compliance': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRuleTypeLabel = (type: string) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const filteredRules = rules.filter(rule => {
    if (selectedAgent !== 'all' && rule.company_config_id !== selectedAgent) return false;
    if (selectedType !== 'all' && rule.rule_type !== selectedType) return false;
    return true;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Rules Manager</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure rules that AI agents must follow during calls
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Create Rule
        </button>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900">
        <p className="text-sm">
          About this page: This manages per-company operational rules stored in the <span className="font-mono">agent_rules</span> table (legacy).
          A newer unified rules system exists on the <a href="/ai-voice-agents/rules" className="underline font-medium">Rules</a> page (backed by <span className="font-mono">ai_agent_rules</span>).
          Prefer creating and maintaining new rules in the unified Rules page; keep this page for legacy records and migration.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Agent
            </label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Types</option>
              <option value="must_do">Must Do</option>
              <option value="must_not_do">Must Not Do</option>
              <option value="conditional">Conditional</option>
              <option value="escalation">Escalation</option>
              <option value="compliance">Compliance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading rules...</p>
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No rules found. Create your first rule to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRules.map(rule => (
            <div
              key={rule.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${getRuleTypeColor(rule.rule_type)}`}>
                      {getRuleTypeLabel(rule.rule_type)}
                    </span>
                    {rule.is_active ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <AlertTriangle size={16} className="text-gray-400" />
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900">{rule.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {rule.company_config?.name || 'Unknown Agent'}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(rule)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(rule.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                {rule.description}
              </p>

              <div className="flex justify-between items-center text-xs text-gray-500 pt-3 border-t">
                <div className="flex items-center gap-1">
                  <Tag size={14} />
                  Priority: {rule.priority}
                </div>
                {rule.applies_to_agents && rule.applies_to_agents.length > 0 && (
                  <div className="text-blue-600">
                    +{rule.applies_to_agents.length} agents
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Agent *
                    </label>
                    <select
                      required
                      value={formData.company_config_id}
                      onChange={(e) => setFormData({ ...formData, company_config_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Agent</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rule Type *
                    </label>
                    <select
                      required
                      value={formData.rule_type}
                      onChange={(e) => setFormData({ ...formData, rule_type: e.target.value as AgentRule['rule_type'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="must_do">Must Do</option>
                      <option value="must_not_do">Must Not Do</option>
                      <option value="conditional">Conditional</option>
                      <option value="escalation">Escalation</option>
                      <option value="compliance">Compliance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., Always verify customer identity"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={4}
                      placeholder="Detailed description of the rule and when it applies..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (1-10) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Higher priority rules are enforced first (10 = highest)
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Rule is Active
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentRulesPage;
